import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Lock, FileSearch, Mail, FileWarning,
  Building2, Workflow, AlertTriangle, CheckCircle2, Fingerprint,
  History, GitBranch, Briefcase, ShieldAlert, FileX2, MessageSquareX,
  Search, BarChart3, Sparkles,
} from "lucide-react";
import SEO, { ORG_SCHEMA, breadcrumb, faqSchema } from "../components/SEO";
import StrategicQuestion from "../components/StrategicQuestion";

const FAQ = [
  {
    q: "MetaConnect est-il un outil de détection de fraude ?",
    a: "Non. Notre approche est différente : nous sécurisons les échanges en amont (identification des acteurs, traçabilité, MSSanté) pour réduire les conditions qui rendent la fraude documentaire possible. Les outils de contrôle a posteriori restent à la main des organismes (Assurance Maladie, mutuelles, contrôles internes).",
  },
  {
    q: "Quels types de documents bénéficient de la sécurisation MSSanté ?",
    a: "Tous les documents de santé qui circulent entre professionnels : prescriptions, ordonnances, comptes rendus, justificatifs, demandes d'avis, modifications de prescriptions. Dès lors qu'un document quitte un système métier pour rejoindre un autre acteur, MSSanté garantit l'identification de l'émetteur, la confidentialité, la traçabilité et l'horodatage opposable.",
  },
  {
    q: "En quoi la traçabilité MSSanté est-elle différente d'un email classique ?",
    a: "Un email classique ne garantit ni l'identité réelle de l'émetteur ni l'intégrité du contenu. MSSanté authentifie les correspondants via CPS / e-CPS / Pro Santé Connect, conserve un historique horodaté des transmissions et fournit une piste d'audit opposable en cas de litige ou d'enquête.",
  },
  {
    q: "Comment MetaConnect aide-t-il à structurer les flux documentaires ?",
    a: "Nous accompagnons la création de domaines MSSanté dédiés, la mise en place de boîtes organisationnelles par service, la définition des règles d'accès et la migration progressive des flux existants (prescriptions, comptes rendus, justificatifs) vers des canaux sécurisés et tracés.",
  },
];

const SCHEMA = [
  ORG_SCHEMA,
  breadcrumb([
    { name: "Accueil", url: "/" },
    { name: "Lutte contre la fraude documentaire en santé", url: "/lutte-fraude-sante" },
  ]),
  faqSchema(FAQ),
];

export default function LutteFraudeSante() {
  return (
    <div className="bg-[var(--mc-navy-950)]" data-testid="page-lutte-fraude-sante">
      <SEO
        title="Lutte contre la fraude documentaire en santé · MetaConnect"
        description="Sécurisez les échanges, les prescriptions et la traçabilité des documents de santé. La MSSanté comme levier de sécurisation en amont."
        canonical="/lutte-fraude-sante"
        schema={SCHEMA}
      />

      {/* HERO */}
      <section className="relative overflow-hidden hex-grid pt-32 pb-20 lg:pt-40 lg:pb-28" data-testid="section-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full"
               style={{ background: "radial-gradient(circle, rgba(110,231,197,0.18), transparent 60%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 glow-pill px-4 py-1.5 rounded-full text-[12px]" data-testid="hero-eyebrow">
              <ShieldAlert size={13} className="text-emerald-600" />
              Lutte contre la fraude documentaire · Santé
            </div>

            <h1 className="font-display font-semibold text-[#0F2B5B] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] mt-6">
              Lutter contre la{" "}
              <span className="bg-gradient-to-r from-[#7FB3FF] via-[#4F8FFF] to-[#2D7BFF] bg-clip-text text-transparent">
                fraude documentaire
              </span>{" "}
              en santé
            </h1>

            <p className="mt-6 text-[16px] lg:text-[17px] leading-relaxed text-[#475569] max-w-2xl fade-up delay-1">
              <strong className="text-[#0F2B5B]">Réduire les zones de risque en sécurisant les échanges,
              les prescriptions et la traçabilité des documents de santé.</strong>
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3 fade-up delay-3">
              <Link to="/demo" data-testid="hero-cta-contact"
                    className="btn-primary px-6 py-3 rounded-full text-[14px] font-medium inline-flex items-center gap-2">
                Échanger sur votre besoin <ArrowRight size={16} />
              </Link>
              <Link to="/mssante-assureurs" data-testid="hero-cta-assureurs"
                    className="btn-ghost px-6 py-3 rounded-full text-[14px] font-medium">
                Pour les assureurs & mutuelles
              </Link>
            </div>

            <div className="mt-12 flex items-center gap-6 text-[12px] text-[#475569] flex-wrap fade-up delay-4">
              <Stat icon={<ShieldCheck size={16} />} label="Sécurisation en amont" />
              <Stat icon={<History size={16} />} label="Traçabilité opposable" />
              <Stat icon={<Fingerprint size={16} />} label="Identification CPS / PSC" />
            </div>
          </div>

          <div className="lg:col-span-5 fade-up delay-2">
            <ApproachVisual />
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* STRATEGIC QUESTION (V3) */}
      <StrategicQuestion />

      <div className="section-divider" />

      {/* INTRODUCTION */}
      <section className="relative hex-soft py-24 lg:py-32" data-testid="section-intro">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <SectionEyebrow>Le contexte</SectionEyebrow>
          <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
            La fraude en santé ne repose pas uniquement sur des{" "}
            <span className="text-[#0369A1]">actes intentionnels</span>
          </h2>
          <div className="mt-6 grid gap-4 text-[15px] lg:text-[16px] text-[#475569] leading-relaxed">
            <p>
              Elle est souvent facilitée par des circuits de transmission fragiles :
            </p>
            <ul className="grid gap-2 ml-2">
              <Bullet>Documents envoyés par email non sécurisé</Bullet>
              <Bullet>Prescriptions difficiles à authentifier</Bullet>
              <Bullet>Absence de preuve de transmission</Bullet>
              <Bullet>Multiplicité des canaux non maîtrisés</Bullet>
            </ul>
            <p>
              Dans un environnement de santé numérique en pleine structuration, la sécurisation des
              échanges devient un enjeu majeur pour <strong className="text-[#0F2B5B]">fiabiliser les
              parcours, protéger les acteurs et renforcer la confiance dans les données</strong>.
            </p>
            <p>
              L'Assurance Maladie met en avant la nécessité de sécuriser les prescriptions, fiabiliser
              les échanges et lutter contre les fausses pratiques impactant les dépenses de santé.
            </p>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* RISK ZONES */}
      <section className="relative py-24 lg:py-32 hex-grid" data-testid="section-risks">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <SectionEyebrow>Zones de risque</SectionEyebrow>
            <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
              Les zones de risque <span className="text-[#0369A1]">aujourd'hui</span>
            </h2>
          </div>

          <div className="mt-14 grid lg:grid-cols-3 gap-5 lg:gap-6">
            <RiskZone
              icon={<FileWarning size={22} />}
              title="Des documents difficiles à vérifier"
              items={[
                "Ordonnances transmises hors cadre sécurisé",
                "Justificatifs envoyés par email classique",
                "Documents modifiables ou non tracés",
              ]}
              outcome="Difficulté à vérifier l'origine et l'intégrité"
            />
            <RiskZone
              icon={<MessageSquareX size={22} />}
              title="Des échanges non sécurisés"
              items={[
                "Utilisation de messageries personnelles",
                "Absence d'identification claire des correspondants",
                "Perte d'information dans les chaînes d'échanges",
              ]}
              outcome="Perte de fiabilité et multiplication des anomalies"
            />
            <RiskZone
              icon={<History size={22} />}
              title="Une traçabilité insuffisante"
              items={[
                "Pas d'historique fiable",
                "Absence de preuve d'envoi ou de réception",
                "Difficulté à reconstituer un parcours",
              ]}
              outcome="Impossibilité d'auditer correctement les échanges"
            />
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* MSSANTÉ AS STRUCTURING CHANNEL */}
      <section className="relative py-24 lg:py-32 hex-soft" data-testid="section-channel">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <SectionEyebrow>La réponse</SectionEyebrow>
              <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
                Sécuriser les échanges pour{" "}
                <span className="text-[#0369A1]">réduire les risques</span>
              </h2>
              <p className="mt-5 text-[15px] text-[#475569] leading-relaxed">
                La solution ne repose pas uniquement sur le contrôle a posteriori.
                Elle repose sur <strong className="text-[#0F2B5B]">la sécurisation en amont des flux</strong>.
              </p>
              <p className="mt-3 text-[14px] text-[#475569] leading-relaxed">
                Les bonnes pratiques en télésanté imposent l'utilisation de messageries sécurisées,
                la vérification des correspondants et la traçabilité des échanges.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="mc-card p-7 lg:p-8 border-emerald-200">
                <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-600 mb-2 flex items-center gap-2">
                  <Lock size={13} /> MSSanté · un canal structurant
                </div>
                <p className="text-[15px] text-[#0F2B5B]">
                  La messagerie sécurisée MSSanté permet :
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mt-5">
                  <ChannelCard icon={<Fingerprint size={18} />} title="Identifier les professionnels"
                               desc="Authentification CPS / e-CPS / Pro Santé Connect, attribution opposable de chaque message." />
                  <ChannelCard icon={<ShieldCheck size={18} />} title="Sécuriser les transmissions"
                               desc="Chiffrement bout-en-bout conforme aux référentiels ANS." />
                  <ChannelCard icon={<History size={18} />} title="Tracer les échanges"
                               desc="Horodatage, preuve d'envoi/réception, conservation selon les durées légales." />
                  <ChannelCard icon={<ShieldAlert size={18} />} title="Garantir un cadre conforme"
                               desc="Espace de Confiance MSSanté, hébergement HDS, conformité RGPD." />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* APPROACH COMPARISON */}
      <section className="relative py-24 lg:py-32 hex-grid" data-testid="section-approach">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <SectionEyebrow>Notre approche</SectionEyebrow>
            <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
              Une approche <span className="text-[#0369A1]">différente</span> de la lutte
              contre la fraude
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-7">
            {/* Classique */}
            <div className="mc-card p-7 lg:p-8" data-testid="card-approach-classic">
              <div className="flex items-start justify-between">
                <div className="grid place-items-center h-12 w-12 rounded-xl bg-[rgba(255,170,80,0.08)] border border-[rgba(255,170,80,0.30)] text-[#FFC97D]">
                  <Search size={22} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#475569]">Classique</span>
              </div>
              <h3 className="mt-6 font-display text-[#0F2B5B] text-2xl">Contrôler après coup</h3>
              <p className="mt-4 text-[14px] text-[#475569] leading-relaxed">
                Détection a posteriori, audits documentaires, contrôles ciblés. Indispensable, mais
                arrive après que le risque s'est concrétisé.
              </p>
            </div>

            {/* MetaConnect */}
            <div className="mc-card p-7 lg:p-8 border-emerald-200" data-testid="card-approach-metaconnect">
              <div className="flex items-start justify-between">
                <div className="grid place-items-center h-12 w-12 rounded-xl bg-[rgba(110,231,197,0.10)] border border-emerald-200 text-emerald-600">
                  <ShieldCheck size={22} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-600">MetaConnect</span>
              </div>
              <h3 className="mt-6 font-display text-[#0F2B5B] text-2xl">
                Sécuriser <span className="text-emerald-600">avant</span> que le problème n'apparaisse
              </h3>
              <p className="mt-4 text-[14px] text-[#475569] leading-relaxed">
                Réduire les conditions qui rendent la fraude ou les anomalies possibles, en amont de
                la chaîne documentaire :
              </p>
              <ul className="mt-5 grid gap-2 text-[13.5px] text-[#334155]">
                <Bullet>Échanges hors cadre</Bullet>
                <Bullet>Documents non sécurisés</Bullet>
                <Bullet>Absence de traçabilité</Bullet>
                <Bullet>Circuits non structurés</Bullet>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* WHAT METACONNECT BUILDS */}
      <section className="relative py-24 lg:py-32 hex-soft" data-testid="section-builds">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <SectionEyebrow>Mise en œuvre</SectionEyebrow>
            <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
              Ce que MetaConnect{" "}
              <span className="text-[#0369A1]">met en place</span>
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-7">
            <BuildBlock
              icon={<Building2 size={22} />}
              title="Structuration des boîtes MSSanté"
              items={[
                "Boîtes organisationnelles",
                "Gestion des accès",
                "Répartition des usages",
              ]}
            />
            <BuildBlock
              icon={<GitBranch size={22} />}
              title="Sécurisation des flux documentaires"
              items={[
                "Prescriptions",
                "Comptes rendus",
                "Justificatifs",
                "Échanges professionnels",
              ]}
            />
            <BuildBlock
              icon={<History size={22} />}
              title="Traçabilité des échanges"
              items={[
                "Identification des acteurs",
                "Historique des transmissions",
                "Preuve d'envoi",
              ]}
            />
            <BuildBlock
              icon={<Workflow size={22} />}
              title="Mise en place de domaines MSSanté"
              items={[
                "Structuration des identités numériques",
                "Cohérence des échanges",
                "Lisibilité pour les partenaires",
              ]}
            />
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* BENEFITS */}
      <section className="relative py-24 lg:py-32 hex-grid" data-testid="section-benefits">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <SectionEyebrow>Bénéfices</SectionEyebrow>
            <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
              Pour les <span className="text-[#0369A1]">acteurs de santé</span> et les
              organismes
            </h2>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Benefit icon={<FileSearch size={18} />} label="Fiabilité accrue des documents" />
            <Benefit icon={<BarChart3 size={18} />} label="Réduction des anomalies" />
            <Benefit icon={<Fingerprint size={18} />} label="Meilleure identification des acteurs" />
            <Benefit icon={<Search size={18} />} label="Amélioration des audits" />
            <Benefit icon={<ShieldCheck size={18} />} label="Sécurisation des parcours" />
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* STRATEGIC CLOSER */}
      <section className="relative py-24 lg:py-32 hex-soft" data-testid="section-closer">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <SectionEyebrow center>Une brique essentielle</SectionEyebrow>
          <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
            Une brique essentielle du{" "}
            <span className="text-[#0369A1]">numérique en santé</span>
          </h2>
          <p className="mt-6 text-[15px] text-[#475569] leading-relaxed">
            Dans un système où les données circulent de plus en plus :
          </p>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            <ClosingPoint icon={<Lock size={18} />} text="La sécurité des échanges devient un prérequis" />
            <ClosingPoint icon={<History size={18} />} text="La traçabilité devient indispensable" />
            <ClosingPoint icon={<Sparkles size={18} />} text="La structuration des flux devient stratégique" />
          </div>
          <p className="mt-10 text-[15px] lg:text-[16px] text-[#334155]">
            La lutte contre la fraude passe aussi par une <strong className="text-emerald-600">meilleure
            organisation des échanges</strong>.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden py-24 lg:py-32 bg-[var(--mc-navy-900)]" data-testid="section-final-cta">
        <div className="absolute inset-0 pointer-events-none opacity-60">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full"
               style={{ background: "radial-gradient(circle, rgba(45,123,255,0.18), transparent 60%)" }} />
        </div>
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <SectionEyebrow center>Prochaine étape</SectionEyebrow>
          <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mt-4">
            Passez à des échanges{" "}
            <span className="text-[#0369A1]">sécurisés et tracés</span>
          </h2>
          <p className="mt-5 text-[15px] text-[#475569] max-w-3xl mx-auto leading-relaxed">
            Vous souhaitez sécuriser vos transmissions, structurer vos flux documentaires et réduire
            les risques liés aux échanges non sécurisés ? MetaConnect vous accompagne dans la mise
            en place d'une stratégie MSSanté adaptée à votre organisation.
          </p>
          <div className="mt-9 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/demo" data-testid="cta-final-contact"
                  className="btn-primary px-7 py-3.5 rounded-full text-[14px] font-medium inline-flex items-center gap-2">
              Échanger sur votre besoin <ArrowRight size={16} />
            </Link>
            <Link to="/mssante-assureurs" data-testid="cta-final-assureurs"
                  className="btn-ghost px-7 py-3.5 rounded-full text-[14px] font-medium inline-flex items-center gap-2">
              <Briefcase size={14} /> Pour les assureurs & mutuelles
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-24 lg:py-32 hex-soft" data-testid="section-faq">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <SectionEyebrow>Questions fréquentes</SectionEyebrow>
          <h2 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight mt-4 mb-10">
            Vous vous demandez peut-être…
          </h2>
          <div className="grid gap-4">
            {FAQ.map((item, idx) => (
              <details key={item.q} className="mc-card p-5 lg:p-6 group" data-testid={`faq-${idx}`}>
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <h3 className="font-display text-[#0F2B5B] text-[16px] lg:text-[17px] leading-snug">
                    {item.q}
                  </h3>
                  <span className="text-[#0369A1] shrink-0 mt-1 transition-transform group-open:rotate-45">
                    <ArrowRight size={16} className="rotate-45" />
                  </span>
                </summary>
                <p className="mt-4 text-[14px] text-[#475569] leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------- Sub components ---------- */
function Stat({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-2 text-[#0F2B5B]">
      {icon}<span>{label}</span>
    </span>
  );
}

function SectionEyebrow({ children, center = false }) {
  return (
    <div className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#0369A1] ${center ? "" : ""}`}>
      <span className="h-px w-7 bg-[var(--mc-blue-400)]" />
      {children}
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2.5">
      <CheckCircle2 size={15} className="mt-0.5 text-[var(--mc-blue-400)] shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function RiskZone({ icon, title, items, outcome }) {
  return (
    <div className="mc-card p-7 lg:p-8 flex flex-col" data-testid={`risk-${title.toLowerCase().replace(/\s+/g, "-").slice(0, 24)}`}>
      <div className="grid place-items-center h-11 w-11 rounded-lg bg-[rgba(255,170,80,0.08)] border border-[rgba(255,170,80,0.30)] text-[#FFC97D]">
        {icon}
      </div>
      <h3 className="mt-5 font-display text-[#0F2B5B] text-[19px] leading-snug">{title}</h3>
      <ul className="mt-4 grid gap-2 text-[13.5px] text-[#475569] flex-1">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[#FFC97D] mt-2 shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6 pt-4 border-t border-[rgba(127,179,255,0.10)] flex items-start gap-2 text-[12.5px] text-[#FFD9A8]">
        <ArrowRight size={13} className="mt-0.5 text-[#FFC97D] shrink-0" />
        <span><span className="text-[#FFC97D]">Résultat :</span> {outcome}</span>
      </div>
    </div>
  );
}

function ChannelCard({ icon, title, desc }) {
  return (
    <div className="rounded-lg border border-[rgba(127,179,255,0.14)] bg-[#F8FAFC] p-4">
      <div className="flex items-center gap-2.5 text-emerald-600">
        {icon}
        <h4 className="font-display text-[#0F2B5B] text-[15px]">{title}</h4>
      </div>
      <p className="mt-2 text-[13px] text-[#475569] leading-relaxed">{desc}</p>
    </div>
  );
}

function BuildBlock({ icon, title, items }) {
  return (
    <div className="mc-card p-7 lg:p-8">
      <div className="grid place-items-center h-12 w-12 rounded-xl bg-[rgba(45,123,255,0.15)] border border-[rgba(127,179,255,0.28)] text-[#0369A1]">
        {icon}
      </div>
      <h3 className="mt-5 font-display text-[#0F2B5B] text-[19px] leading-snug">{title}</h3>
      <ul className="mt-4 grid gap-2 text-[14px] text-[#334155]">
        {items.map((it) => <Bullet key={it}>{it}</Bullet>)}
      </ul>
    </div>
  );
}

function Benefit({ icon, label }) {
  return (
    <div className="rounded-lg border border-emerald-200 bg-[rgba(110,231,197,0.04)] p-4 flex items-start gap-3">
      <span className="grid place-items-center h-8 w-8 rounded-md bg-[rgba(110,231,197,0.10)] border border-emerald-200 text-emerald-600 shrink-0">
        {icon}
      </span>
      <span className="text-[13px] text-[#334155] leading-snug">{label}</span>
    </div>
  );
}

function ClosingPoint({ icon, text }) {
  return (
    <div className="mc-card p-5 flex items-start gap-3 text-left">
      <span className="grid place-items-center h-9 w-9 rounded-lg bg-[#EFF6FF] border border-[rgba(127,179,255,0.28)] text-[#0369A1] shrink-0">
        {icon}
      </span>
      <span className="text-[13.5px] text-[#334155] leading-relaxed">{text}</span>
    </div>
  );
}

function ApproachVisual() {
  return (
    <div className="mc-card p-7 lg:p-8 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-40"
           style={{ background: "radial-gradient(circle at 70% 30%, rgba(110,231,197,0.18), transparent 50%)" }} />
      <div className="relative grid gap-4">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#0369A1] flex items-center gap-2">
          <ShieldCheck size={13} /> Approche en amont
        </div>

        {/* Before */}
        <div className="rounded-lg border border-[rgba(255,170,80,0.30)] bg-[rgba(255,170,80,0.04)] p-4">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#FFC97D] mb-2 flex items-center gap-2">
            <AlertTriangle size={12} /> Sans MSSanté
          </div>
          <ul className="space-y-1.5 text-[12.5px] text-[#475569]">
            <li className="flex items-center gap-2">
              <FileX2 size={12} className="text-[#FFC97D] shrink-0" />
              Email classique · canal incertain
            </li>
            <li className="flex items-center gap-2">
              <FileWarning size={12} className="text-[#FFC97D] shrink-0" />
              Documents non tracés
            </li>
            <li className="flex items-center gap-2">
              <Search size={12} className="text-[#FFC97D] shrink-0" />
              Audit difficile
            </li>
          </ul>
        </div>

        <div className="flex justify-center">
          <ArrowRight size={18} className="text-[#0369A1]" />
        </div>

        {/* After */}
        <div className="rounded-lg border border-emerald-200 bg-[rgba(110,231,197,0.06)] p-4">
          <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-600 mb-2 flex items-center gap-2">
            <ShieldCheck size={12} /> Avec MSSanté MetaConnect
          </div>
          <ul className="space-y-1.5 text-[12.5px] text-[#0F2B5B]/95">
            <li className="flex items-center gap-2">
              <Mail size={12} className="text-emerald-600 shrink-0" />
              Canal MSSanté · chiffré · annuaire ANS
            </li>
            <li className="flex items-center gap-2">
              <Fingerprint size={12} className="text-emerald-600 shrink-0" />
              Émetteur authentifié
            </li>
            <li className="flex items-center gap-2">
              <History size={12} className="text-emerald-600 shrink-0" />
              Historique opposable
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
